(function () {
  'use strict';

  const config = Object.assign({ middlewareUrl: '', enabled: false }, window.BB_CUSTOMIZATION_CONFIG || {});
  const property = (item, name) => item.properties && item.properties[name];
  let protectedCart = null;
  let protectedValidation = null;
  let cartLockState = { locked: false, message: '' };

  function productFormScope(button) {
    return button.closest('.product-form') || button.closest('product-info') || document;
  }

  function selectedVariantId(button) {
    const scope = productFormScope(button);
    return scope.querySelector('select[name="id"]')?.value ||
      scope.querySelector('input[name="id"]')?.value ||
      new URLSearchParams(location.search).get('variant');
  }

  function selectedQuantity(button) {
    const input = productFormScope(button).querySelector('input[name="quantity"], [data-quantity-input], .quantity__input');
    return Math.max(1, Number.parseInt(input?.value || '1', 10) || 1);
  }

  function productHandle() {
    return document.querySelector('meta[name="product-handle"]')?.content ||
      location.pathname.match(/\/products\/([^/]+)/)?.[1] || '';
  }

  function bindCustomizeButton() {
    document.querySelectorAll('[data-customize-product]').forEach(button => {
      if (button.dataset.bbBound) return;
      button.dataset.bbBound = 'true';
      button.addEventListener('click', event => {
        event.preventDefault();
        if (!config.enabled) {
          alert('Product customization is temporarily paused while we complete a reliability upgrade. Please try again shortly.');
          return;
        }
        const variantId = selectedVariantId(button);
        const handle = productHandle();
        if (!variantId || !handle) {
          alert('Please select a size and colour before customizing.');
          return;
        }
        const url = new URL('/customize', config.middlewareUrl);
        url.searchParams.set('product_handle', handle);
        url.searchParams.set('variant_id', variantId);
        url.searchParams.set('quantity', String(selectedQuantity(button)));
        location.assign(url.toString());
      });
    });
  }

  function bindChooseOptionsFallback() {
    if (document.documentElement.dataset.bbChooseOptionsFallbackBound) return;
    document.documentElement.dataset.bbChooseOptionsFallbackBound = 'true';
    document.addEventListener('click', event => {
      const button = event.target.closest('button[aria-controls^="QuickView-"]');
      if (!button || document.querySelector('.section-group-overlay-quick-view')) return;
      const modal = document.getElementById(button.getAttribute('aria-controls'));
      if (!modal?.dataset.productUrl) return;
      const productUrl = new URL(modal.dataset.productUrl, location.origin);
      if (productUrl.origin !== location.origin || !productUrl.pathname.startsWith('/products/')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      location.assign(productUrl.toString());
    }, true);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  }

  async function validateCart(cart) {
    const response = await fetch(`${config.middlewareUrl}/api/cart/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items })
    });
    if (!response.ok) throw new Error('Customization validation is unavailable');
    return response.json();
  }

  function checkoutControls() {
    return document.querySelectorAll('[name="checkout"], .additional-checkout-buttons button, [data-testid*="Checkout"]');
  }

  function setCheckoutLocked(locked, message, remember = true) {
    if (remember) cartLockState = { locked, message: message || '' };
    checkoutControls().forEach(control => {
      control.disabled = locked;
      control.setAttribute('aria-disabled', String(locked));
    });
    let banner = document.querySelector('[data-bb-cart-warning]');
    // A silent lock is used only during the brief validation request. Never
    // flash an error-style banner while an ordinary storefront page loads.
    if (!banner && locked && message && location.pathname.startsWith('/cart')) {
      banner = document.createElement('div');
      banner.dataset.bbCartWarning = 'true';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = 'margin:16px 0;padding:14px;border:1px solid #a11;background:#fff2f2;color:#711;border-radius:6px';
      (document.querySelector('form[action="/cart"]') || document.querySelector('main'))?.prepend(banner);
    }
    if (banner) {
      const nextMessage = message || '';
      if (banner.textContent !== nextMessage) banner.textContent = nextMessage;
      if (banner.hidden !== !locked) banner.hidden = !locked;
    }
  }

  function textFromHtml(value) {
    const template = document.createElement('template');
    template.innerHTML = String(value || '').replace(/<br\s*\/?>/gi, ' · ');
    return String(template.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function customizationDisplay(cart, customizationId, group, index) {
    const garments = cart.items.filter(item =>
      cartCustomizationId(item) === customizationId && property(item, '_bb_role') === 'garment'
    );
    const productNames = [...new Set(garments.map(item => item.product_title || item.title).filter(Boolean))];
    const variants = garments.map(item => {
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : property(item, '_bb_size');
      return `${variant || 'Customized garment'} × ${item.quantity}`;
    });
    return {
      title: group?.display?.productName || productNames.join(', ') || `Customized design ${index + 1}`,
      details: textFromHtml(group?.display?.options) || variants.join(' · '),
      thumbnail: group?.display?.thumbnail || ''
    };
  }

  function cartCustomizationId(item) {
    return property(item, '_bb_customization_id');
  }

  async function removeBundle(cart, customizationId) {
    const updates = {};
    const bundleItems = cart.items.filter(item => cartCustomizationId(item) === customizationId);
    bundleItems.forEach(item => { updates[item.key] = 0; });
    if (!bundleItems.length) return;
    const response = await fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error('Customization could not be removed');
    const version = Number(property(bundleItems[0], '_bb_version'));
    try {
      await fetch(`${config.middlewareUrl}/api/customization/${encodeURIComponent(customizationId)}/remove`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version, items: bundleItems })
      });
    } catch (error) {
      // The Shopify removal succeeded. Middleware records and supplier cleanup
      // are retried operationally; never put an orphaned line back in the cart.
    }
    location.reload();
  }

  function renderCustomizationActions(cart, validatedGroups) {
    document.querySelector('[data-bb-customization-actions]')?.remove();
    const ids = [...new Set(cart.items.map(cartCustomizationId).filter(Boolean))];
    if (!ids.length) return;
    const groupsById = new Map((validatedGroups || []).map(group => [group.customizationId, group]));
    const panel = document.createElement('section');
    panel.dataset.bbCustomizationActions = 'true';
    panel.setAttribute('aria-label', 'Customized items');
    panel.style.cssText = 'margin:16px 0;padding:18px;border:1px solid rgba(0,0,0,.15);border-radius:12px;background:#fff';
    const heading = document.createElement('strong');
    heading.textContent = ids.length === 1 ? 'Your saved design' : 'Your saved designs';
    panel.appendChild(heading);
    ids.forEach((id, index) => {
      const group = groupsById.get(id);
      const display = customizationDisplay(cart, id, group, index);
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:12px';
      if (display.thumbnail) {
        const preview = document.createElement('img');
        preview.src = display.thumbnail;
        preview.alt = `Preview of design ${index + 1}`;
        preview.loading = 'lazy';
        preview.width = 72;
        preview.height = 72;
        preview.style.cssText = 'width:72px;height:72px;object-fit:contain;border:1px solid rgba(0,0,0,.12);border-radius:8px;background:#f7f7f7';
        row.appendChild(preview);
      }
      const label = document.createElement('span');
      label.style.cssText = 'display:flex;flex:1 1 240px;min-width:0;flex-direction:column;gap:3px';
      const designName = document.createElement('strong');
      designName.textContent = `Design ${index + 1}: ${display.title}`;
      label.appendChild(designName);
      if (display.details) {
        const details = document.createElement('small');
        details.textContent = display.details;
        details.style.cssText = 'color:#555;line-height:1.4';
        label.appendChild(details);
      }
      row.appendChild(label);
      if (group?.editLink) {
        const edit = document.createElement('a');
        edit.href = group.editLink;
        edit.textContent = 'Edit design';
        edit.style.cssText = 'display:inline-block;padding:10px 14px;border:1px solid #111;border-radius:8px;color:#111;text-decoration:none';
        row.appendChild(edit);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = 'Remove design';
      remove.dataset.bbRemoveCustomization = id;
      remove.style.cssText = 'padding:10px 14px;border:0;border-radius:8px;background:#761515;color:#fff;cursor:pointer';
      row.appendChild(remove);
      panel.appendChild(row);
    });
    (document.querySelector('form[action="/cart"]') || document.querySelector('main'))?.prepend(panel);
  }

  function cartItemForRow(row, cart) {
    if (!row || !cart) return null;
    const remove = row.querySelector('a[href*="/cart/change"], [data-cart-remove]');
    const key = row.dataset.itemKey || new URL(remove?.href || location.href).searchParams.get('id');
    return cart.items.find(candidate => candidate.key === key || String(candidate.variant_id) === String(key)) || null;
  }

  function lockCustomizedQuantityInputs(cart) {
    document.querySelectorAll('[data-item-key], cart-item, .cart-item, tr').forEach(row => {
      const item = cartItemForRow(row, cart);
      if (!item || !cartCustomizationId(item)) return;
      row.querySelectorAll('input[name="updates[]"], [data-quantity-input], .quantity__input').forEach(input => {
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        input.title = 'Change customized quantities in the designer';
      });
    });
  }

  function reapplyProtectedCartUi() {
    if (!protectedCart) return;
    lockCustomizedQuantityInputs(protectedCart);
    setCheckoutLocked(cartLockState.locked, cartLockState.message, false);
    if (location.pathname.startsWith('/cart') && protectedValidation &&
      !document.querySelector('[data-bb-customization-actions]')) {
      renderCustomizationActions(protectedCart, protectedValidation.groups);
    }
  }

  function bindCartInteractions(cart) {
    protectedCart = cart;
    lockCustomizedQuantityInputs(cart);
    if (document.documentElement.dataset.bbCartInteractionsBound) return;
    document.documentElement.dataset.bbCartInteractionsBound = 'true';
    document.addEventListener('click', async event => {
      const explicitRemove = event.target.closest('[data-bb-remove-customization]');
      if (explicitRemove) {
        event.preventDefault();
        const customizationId = explicitRemove.dataset.bbRemoveCustomization;
        if (confirm('Remove this design, its customized garment and its design charge?')) {
          explicitRemove.disabled = true;
          await removeBundle(protectedCart, customizationId);
        }
        return;
      }
      const remove = event.target.closest('a[href*="/cart/change"], [data-cart-remove]');
      const quantityControl = event.target.closest('[name="plus"], [name="minus"], [data-quantity-plus], [data-quantity-minus], button[aria-label^="Increase quantity"], button[aria-label^="Decrease quantity"]');
      const row = event.target.closest('[data-item-key], cart-item, .cart-item, tr');
      if (!row || (!remove && !quantityControl)) return;
      const item = cartItemForRow(row, protectedCart);
      if (!item || !cartCustomizationId(item)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (quantityControl) {
        alert('Please use Edit design to change the quantity, size or colour of a customized item. This keeps the garment and artwork together.');
        return;
      }
      if (confirm('Remove this design, its customized garment and its design charge?')) {
        await removeBundle(protectedCart, cartCustomizationId(item));
      }
    }, true);
  }

  async function protectCart() {
    if (!config.middlewareUrl) return;
    let hasCustomizedItems = false;
    try {
      const cart = await fetchCart();
      const customItems = cart.items.filter(item => cartCustomizationId(item));
      if (!customItems.length) return setCheckoutLocked(false, '');
      hasCustomizedItems = true;
      setCheckoutLocked(true, '');
      bindCartInteractions(cart);
      const validation = await validateCart(cart);
      protectedValidation = validation;
      if (location.pathname.startsWith('/cart')) renderCustomizationActions(cart, validation.groups);
      if (!validation.valid) {
        return setCheckoutLocked(true, 'A saved design no longer matches the items in your cart. Use Edit design to refresh it, or remove the design before checkout.');
      }
      setCheckoutLocked(false, '');
    } catch (error) {
      if (hasCustomizedItems) {
        setCheckoutLocked(true, 'We cannot verify a saved design right now. Checkout is temporarily paused; please try again.');
      }
    }
  }

  function isCartCallbackPage() {
    return location.pathname.replace(/\/+$/, '') === '/pages/cart-callback';
  }

  function initialize() {
    bindCustomizeButton();
    bindChooseOptionsFallback();
    if (!isCartCallbackPage()) protectCart();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  new MutationObserver(() => {
    bindCustomizeButton();
    reapplyProtectedCartUi();
  }).observe(document.documentElement, { childList: true, subtree: true });
}());
